import { InternalUser, OrganizationUserCreate } from '../../features/organizations/types/Organizations'

export const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
export const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{10,})/

export type EditUserFormValues = {
  email: string
  firstName: string
  lastName: string
  title: string
  oadmin: boolean
  /** New users only. */
  sendInvite: boolean
  /** New users only; sent with the invite too, like legacy. */
  password: string
}

export type EditedUserField = 'email' | 'firstName' | 'lastName' | 'title' | 'oadmin'

export const editedUserFields: EditedUserField[] = ['email', 'firstName', 'lastName', 'title', 'oadmin']

export const userFieldLabelKeys: Record<EditedUserField, string> = {
  email: 'internal-csm:edit_user_email',
  firstName: 'internal-csm:edit_user_first_name',
  lastName: 'internal-csm:edit_user_last_name',
  title: 'internal-csm:edit_user_title',
  oadmin: 'internal-csm:edit_user_admin',
}

type TextField = Exclude<EditedUserField, 'oadmin'>
const textFields: TextField[] = ['email', 'firstName', 'lastName', 'title']

/**
 * Legacy's generator can leave out the upper-case letter (its "upper" set includes digits), which then fails the
 * password rule, so it is retried until the result passes.
 */
export const generatePassword = (generate: () => string) => {
  let password = generate()
  for (let attempt = 0; attempt < 50 && !passwordPattern.test(password); attempt++) password = generate()
  return password
}

export const toEditUserFormValues = (user: InternalUser | undefined, password: string): EditUserFormValues => ({
  email: user?.email ?? '',
  firstName: user?.firstName ?? '',
  lastName: user?.lastName ?? '',
  title: user?.title ?? '',
  oadmin: user?.oadmin ?? false,
  sendInvite: !user,
  password,
})

export type EditUserFormError = 'email_required' | 'email_invalid' | 'password_required' | 'password_invalid'

export const getEmailError = (email: string): EditUserFormError | undefined => {
  const trimmed = email.trim()
  if (!trimmed) return 'email_required'
  return emailPattern.test(trimmed) ? undefined : 'email_invalid'
}

/** A password is needed only for a new user who isn't invited by e-mail. */
export const getPasswordError = (values: Pick<EditUserFormValues, 'password' | 'sendInvite'>, isNew: boolean): EditUserFormError | undefined => {
  if (!isNew || values.sendInvite) return undefined
  const trimmed = values.password.trim()
  if (!trimmed) return 'password_required'
  return passwordPattern.test(trimmed) ? undefined : 'password_invalid'
}

export const isEditUserFormValid = (values: EditUserFormValues, isNew: boolean) => !getEmailError(values.email) && !getPasswordError(values, isNew)

export const isEditUserFormChanged = (values: EditUserFormValues, initial: EditUserFormValues) =>
  textFields.some((field) => values[field].trim() !== initial[field].trim()) ||
  values.oadmin !== initial.oadmin ||
  values.sendInvite !== initial.sendInvite ||
  values.password !== initial.password

/** Legacy's add body: typed text trimmed (Angular's inputs trim). */
export const toUserCreate = (organizationId: string, values: EditUserFormValues): OrganizationUserCreate => ({
  id: organizationId,
  email: values.email.trim(),
  password: values.password.trim(),
  firstName: values.firstName.trim(),
  lastName: values.lastName.trim(),
  title: values.title.trim(),
  active: true,
})

/** Legacy's update body: the complete stored user with the dialog's fields. An untouched `null` stays `null`. */
export const toUserUpdate = (user: InternalUser, values: EditUserFormValues): InternalUser => {
  const updated = { ...user, oadmin: values.oadmin }
  textFields.forEach((field) => {
    const typed = values[field].trim()
    if (typed !== (user[field] ?? '')) updated[field] = typed
  })
  return updated
}

/** The dialog's fields that the re-read user doesn't hold; `null` and `''` are the same. */
export const getUnstoredUserFields = (sent: Pick<InternalUser, TextField> | Pick<OrganizationUserCreate, TextField>, stored: InternalUser, admin: boolean) =>
  editedUserFields.filter((field) => {
    if (field === 'oadmin') return stored.oadmin !== admin
    const value = (sent as Record<TextField, string | null>)[field]
    return (value ?? '') !== (stored[field] ?? '')
  })
